import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle, Clock, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthenticatedUser } from '@/hooks/useAuthenticatedUser';
import { supabase } from '@/integrations/supabase/client';

interface ScorekeeperApplication {
  id: string;
  scorekeeper_id: string;
  applicant_name: string;
  message?: string;
  status: 'pending' | 'approved' | 'rejected';
  applied_at: string;
  game_title?: string;
  game_date?: string;
  game_time?: string;
}

interface ScorekeeperAssignmentInterfaceProps {
  gameId: string;
}

export const ScorekeeperAssignmentInterface: React.FC<ScorekeeperAssignmentInterfaceProps> = ({ gameId }) => {
  const [applications, setApplications] = useState<ScorekeeperApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<Set<string>>(new Set());
  const { user } = useAuthenticatedUser();

  useEffect(() => {
    if (gameId) {
      loadApplications();
    }
  }, [gameId]);

  const loadApplications = async () => {
    if (!gameId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('scorekeeper_applications')
        .select(`
          *,
          applicant:scorekeeper_id (
            username,
            first_name,
            last_name
          )
        `)
        .eq('game_id', gameId)
        .eq('status', 'pending')
        .order('applied_at', { ascending: true });

      if (error) throw error;

      const formattedApplications = data?.map((app: any) => ({
        id: app.id,
        scorekeeper_id: app.scorekeeper_id,
        applicant_name: app.applicant?.username || 
          `${app.applicant?.first_name || ''} ${app.applicant?.last_name || ''}`.trim() || 
          'Unknown User',
        message: app.message,
        status: app.status,
        applied_at: app.applied_at
      })) || [];

      setApplications(formattedApplications);
    } catch (error) {
      console.error('Error loading applications:', error);
      toast.error('Failed to load applications');
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const assignScorekeeper = async (applicationId: string, scorekeeperId: string) => {
    if (!user?.id) return;

    setProcessing(prev => new Set([...prev, applicationId]));

    try {
      const { scorekeeperApprovalService } = await import('@/services/scorekeeperApprovalService');
      
      const result = await scorekeeperApprovalService.approveApplication(
        applicationId,
        user.id,
        'venue_owner', // This could be determined dynamically based on user role
        gameId
      );

      if (result.success) {
        toast.success('Scorekeeper assigned successfully!');
        loadApplications();
      } else {
        throw result.error;
      }
    } catch (error: any) {
      console.error('Error assigning scorekeeper:', error);
      toast.error(error?.message || 'Failed to assign scorekeeper');
    } finally {
      setProcessing(prev => {
        const next = new Set(prev);
        next.delete(applicationId);
        return next;
      });
    }
  };

  const rejectApplication = async (applicationId: string) => {
    if (!user?.id) return;

    const reason = prompt('Please provide a reason for rejection:');
    if (!reason) return;

    setProcessing(prev => new Set([...prev, applicationId]));

    try {
      const { scorekeeperApprovalService } = await import('@/services/scorekeeperApprovalService');
      
      const result = await scorekeeperApprovalService.rejectApplication(
        applicationId,
        user.id,
        'venue_owner',
        reason,
        gameId
      );

      if (result.success) {
        toast.success('Application rejected');
        loadApplications();
      } else {
        throw result.error;
      }
    } catch (error: any) {
      console.error('Error rejecting application:', error);
      toast.error(error?.message || 'Failed to reject application');
    } finally {
      setProcessing(prev => {
        const next = new Set(prev);
        next.delete(applicationId);
        return next;
      });
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-muted rounded w-1/2"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (applications.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Applications</h3>
          <p className="text-muted-foreground">
            No scorekeeper applications for this game yet.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Scorekeeper Applications</h3>
        <Badge variant="outline">
          {applications.length} applications
        </Badge>
      </div>

      {applications.map((application) => {
        const isProcessing = processing.has(application.id);

        return (
          <Card key={application.id}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">
                    {application.applicant_name}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Applied: {new Date(application.applied_at).toLocaleDateString()}
                  </p>
                </div>
                <Badge variant="secondary">
                  <Clock className="h-4 w-4 mr-1" />
                  Pending
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {application.message && (
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm">"{application.message}"</p>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => assignScorekeeper(application.id, application.scorekeeper_id)}
                    disabled={isProcessing}
                    className="flex-1"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Assign
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => rejectApplication(application.id)}
                    disabled={isProcessing}
                    className="flex-1"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};